import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, CircleDashed, Database, Info, Minus, XCircle } from "lucide-react";

export const PRIORITY_STYLES = {
  very_high: "bg-rose-50 text-rose-700 border-rose-200",
  high: "bg-amber-50 text-amber-800 border-amber-200",
  medium: "bg-slate-50 text-slate-700 border-slate-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
  review: "bg-slate-50 text-slate-500 border-slate-200",
};

export const PRIORITY_LABELS = {
  very_high: "Very high", high: "High", medium: "Medium", low: "Low", review: "Review",
};

export const PriorityBadge = ({ priority }) => (
  <Badge variant="outline" data-testid="priority-badge" className={`h-6 px-2 text-xs font-medium ${PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium}`}>
    {PRIORITY_LABELS[priority] || priority}
  </Badge>
);

export const SEVERITY_STYLES = {
  critical: "bg-rose-50 text-rose-700 border-rose-200",
  high: "bg-amber-50 text-amber-800 border-amber-200",
  medium: "bg-slate-100 text-slate-700 border-slate-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
  info: "bg-slate-50 text-slate-500 border-slate-200",
};

export const SeverityBadge = ({ severity }) => (
  <Badge variant="outline" data-testid="severity-badge" className={`h-6 px-2 text-xs font-medium capitalize ${SEVERITY_STYLES[severity] || SEVERITY_STYLES.medium}`}>
    {severity}
  </Badge>
);

export const TASK_STATUS_STYLES = {
  draft: "bg-slate-50 text-slate-700 border-slate-200",
  approved: "bg-blue-50 text-blue-700 border-blue-200",
  queued: "bg-slate-50 text-slate-700 border-slate-200",
  running: "bg-sky-50 text-sky-700 border-sky-200",
  analysing: "bg-sky-50 text-sky-700 border-sky-200",
  changing: "bg-sky-50 text-sky-700 border-sky-200",
  testing: "bg-amber-50 text-amber-800 border-amber-200",
  preview_ready: "bg-blue-50 text-blue-700 border-blue-200",
  changes_requested: "bg-amber-50 text-amber-800 border-amber-200",
  approved_production: "bg-emerald-50 text-emerald-700 border-emerald-200",
  deployed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-slate-50 text-slate-500 border-slate-200",
  measuring: "bg-blue-50 text-blue-700 border-blue-200",
};

export const TaskStatusBadge = ({ status }) => (
  <Badge variant="outline" data-testid="task-status-chip" className={`h-6 px-2 text-xs font-medium ${TASK_STATUS_STYLES[status] || TASK_STATUS_STYLES.draft}`}>
    {(status || "").replaceAll("_", " ")}
  </Badge>
);

export const DemoBadge = () => (
  <Badge variant="outline" data-testid="demo-data-badge" className="h-6 px-2 text-xs font-semibold bg-violet-50 text-violet-700 border-violet-200">
    <Database className="h-3 w-3 mr-1" /> DEMO DATA
  </Badge>
);

export const OPP_TYPE_LABELS = {
  striking_distance: "Striking distance", low_ctr: "Low CTR", content_decay: "Content decay",
  cannibalisation: "Cannibalisation", technical: "Technical", internal_links: "Internal links",
  content_gap: "Content gap", location: "Location", conversion: "Conversion", indexing: "Indexing",
};

export const StatCard = ({ label, value, sub, delta, deltaGoodWhen = "up", testId }) => {
  let deltaEl = null;
  if (delta !== null && delta !== undefined && isFinite(delta) && delta !== 0) {
    const up = delta > 0;
    const good = deltaGoodWhen === "up" ? up : !up;
    deltaEl = (
      <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${good ? "text-emerald-700" : "text-rose-700"}`}>
        {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
        {Math.abs(delta).toLocaleString(undefined, { maximumFractionDigits: 1 })}
      </span>
    );
  } else if (delta === 0) {
    deltaEl = <span className="inline-flex items-center text-xs text-muted-foreground"><Minus className="h-3 w-3" /></span>;
  }
  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span data-testid={testId} className="text-2xl font-semibold tabular-nums">{value ?? "—"}</span>
          {deltaEl}
        </div>
        {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
};

export const EmptyState = ({ icon: Icon = CircleDashed, title, message, children, testId }) => (
  <div data-testid={testId || "empty-state"} className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-card px-6 py-12 text-center">
    <Icon className="h-8 w-8 text-muted-foreground/50" />
    <h3 className="mt-3 text-sm font-semibold">{title}</h3>
    {message && <p className="mt-1 max-w-md text-sm text-muted-foreground">{message}</p>}
    {children && <div className="mt-4 flex flex-wrap items-center justify-center gap-2">{children}</div>}
  </div>
);

export const StatusIcon = ({ ok, warn }) => {
  if (ok) return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (warn) return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  return <XCircle className="h-4 w-4 text-rose-600" />;
};

export const InfoNote = ({ children }) => (
  <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    <span>{children}</span>
  </div>
);
